// Which parcel_appeals row is THE row for a parcel-year?
//
// ⛔ JURISDICTION ROWS ARE ALTERNATIVE VALUATIONS OF ONE PROPERTY, NOT PARTITIONS OF IT. A Florida parcel
// carries two taxable bases because the non-homestead assessment cap limits non-school assessed value to
// +10% a year while schools are exempt from it: Boynton Beach 435200IN in 2026 is 11,778,423 to Public
// Schools and 10,919,985 to County / Municipality / Special Districts, differing by exactly the capped
// 858,438. Both describe the SAME property. Adding them asserts a 22,698,408 property that does not exist.
//
// Four consumers each answered "which row?" differently — two summed, two matched the literal string
// 'County' — so one parcel could report a doubled taxable value, a blank saving and a correct per-SF figure
// at the same time. This is the one answer.
//
// ⛔ is_primary WAS ALREADY THE DECLARED MECHANISM AND WAS NEVER USED AS ONE. It is written (true on the
// base row, false on every added one), ordered by in both loaders, and filtered on NOWHERE. The actual
// guard was `jurisName === 'County'` in removeJurisdiction — which is wrong for Florida, where the
// non-school base is three authorities and would sensibly be named "County / Municipal / Special". A row's
// name cannot carry its role when the name is free text typed into + Add Jurisdiction.
//
// ⛔ A SINGLE ROW WINS WHATEVER ITS FLAG SAYS. Scanner-written rows set is_primary from a loop index
// (ji === 0), and a row can end up false with no true sibling; requiring the flag would blank a figure
// that is not ambiguous at all. One row is one answer. No backfill is needed for the same reason.
//
// ⛔ AMBIGUITY REFUSES RATHER THAN GUESSING. Several rows and no single flagged one means we do not know
// which base is being asked for, and the two differ by the cap — so a guess is wrong by a real amount in a
// column a client reads. A blank is a question; the school base printed as "the" taxable value is an
// assertion. Same judgement as computeTaxBill's multi-jurisdiction guard and aggregateTax's out-of-band
// refusal. The warning names the parcel-year so the defect is findable rather than merely survived.
export function primaryAppealRow(rows, context = '') {
  const list = (rows || []).filter(Boolean);
  if (!list.length) return null;
  if (list.length === 1) return list[0];

  // ⛔ "NOBODY FLAGGED A ROW" AND "THE COLUMN WAS NOT SELECTED" LOOKED IDENTICAL, AND THE SECOND ONE SHIPPED.
  // src/reports/parcelYearRows.js enumerated its parcel_appeals columns and omitted is_primary, so every row
  // arrived with the key ABSENT, every two-row parcel-year resolved to null, and Proposed / Final / Taxes /
  // Savings rendered as dashes. Nothing errored, and the warning below blamed the DATA — which was correct
  // and fully flagged — sending the reader to look in the wrong place.
  //
  // THE TWO ARE DISTINGUISHABLE WITHOUT A PROXY, and this is the cheap render-path guard known-debt asked
  // for: PostgREST returns every SELECTED column as a key, NULL included, and omits an unselected one from
  // the object entirely. So `'is_primary' in row` separates "selected and empty" (a data question) from
  // "never fetched" (a query bug) with one check and no maintained field list.
  //
  // ⛔ ONLY ABOVE ONE ROW, deliberately. A single row wins whatever its flag says, so an unselected column is
  // genuinely harmless there — firing then would log on every single-row parcel-year in the database, which
  // is all of them but one, and a guard that always fires is one nobody reads.
  //
  // console.error, not warn: a missing column is a developer error with a one-word fix, while unflagged rows
  // are an operational one needing someone to decide which base is primary. Different severities, different
  // remedies, and the console should not make them look alike either.
  if (!list.some((r) => 'is_primary' in r)) {
    console.error(
      `[appealRows] is_primary WAS NOT SELECTED — this is a query bug, not ambiguous data`
      + `${context ? ` (${context})` : ''}. ${list.length} jurisdiction rows arrived without the key at all, `
      + `so no row can be chosen and every figure derived from this parcel-year will render blank. `
      + `Add "is_primary" to the select. Row keys present: ${Object.keys(list[0]).join(', ')}.`
    );
    return null;
  }

  const flagged = list.filter((r) => r.is_primary === true);
  if (flagged.length === 1) return flagged[0];
  console.warn(
    `[appealRows] REFUSING a parcel-year: ${list.length} jurisdiction rows and `
    + `${flagged.length === 0 ? 'none is marked is_primary' : `${flagged.length} are marked is_primary`}`
    + `${context ? ` (${context})` : ''}. Jurisdictions: ${list.map((r) => r.jurisdiction || '—').join(', ')}. `
    + 'These are alternative valuations of one property, so there is no correct single figure to show. '
    + 'Mark exactly one row is_primary.'
  );
  return null;
}

// The same row at another appeal level, matched by JURISDICTION rather than by flag. A saving is
// (proposed − final) on ONE base; taking each level's own primary could subtract the school base from the
// non-school one and call the cap a reduction we won.
export function sameJurisdictionRow(rows, jurisdiction) {
  if (jurisdiction == null) return null;
  return (rows || []).find((r) => r && r.jurisdiction === jurisdiction) || null;
}

// ═══ TAXABLE TOTAL — subtotal LESS the assessment cap LESS exemptions ═══
//
// ⛔ EXTRACTED BECAUSE IT GAINED A THIRD TERM AND PARSED THEM TWO DIFFERENT WAYS. The line read
// `(row.taxable_subtotal || 0) - dedSumFor(...)`: the subtotal raw, the other term through the
// component's gNum. A subtotal arriving as the string '10,919,985' makes `'10,919,985' - 858438`
// evaluate to NaN, and NaN || null stores null — a blank Taxable Total with no cause on screen. In
// practice ValueCell commits numbers so it never fired, but adding a term to a line with two parsing
// rules is how that stops being true. One parse here, for all three. marketTotalOf in utils/perSf.js
// is the precedent for taking one of these formulas out of the component.
//
// ⛔ THE CAP IS SUBTRACTED BEFORE EXEMPTIONS, which is why it is its own column rather than a
// Deductions line. Florida's non-homestead assessment cap limits NON-SCHOOL assessed value to +10%
// a year (schools are exempt); exemptions then apply to what is left. Boynton Beach 435200IN 2026:
// prior 9,927,259 x 1.10 = 10,919,985, a reduction of 858,438 from the 11,778,423 the schools tax.
//
// ⛔ ONE FORMULA FOR EVERY RECOMPUTE (Philip, 2026-10-11). Editing a Deductions line used to recompute the total
// as subtotal − deductions with its own inline arithmetic, LEAVING THE CAP OUT, while editing a value cell used this
// function and kept it. Tampa 1124088000 2021 County: subtotal 1,527,443, cap 3,731, Government exemption 1,523,712
// → $0, but the deduction edit stored 1,527,443 − 1,523,712 = $3,731. Every recompute now comes here.
//
// ⛔ A COMPUTED $0 IS $0 (Philip, 2026-10-11). This ended `|| null`, so a fully exempt column — subtotal present,
// everything deducted — stored null, read "—", and the card then demanded a rate to tax it. Now: no subtotal → null
// (no value); a subtotal present → the difference, $0 included. Rows already stored are untouched until edited.
const asNum = (v) => (typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, '')) || 0);
const blank = (v) => v == null || String(v).trim() === '';

export function taxableTotalOf(row, deductionSum = 0) {
  if (!row || blank(row.taxable_subtotal)) return null;
  return asNum(row.taxable_subtotal) - asNum(row.cap_deduction) - asNum(deductionSum);
}

// ═══ A PER-JURISDICTION RATE, IN THE UNIT IT WAS ENTERED IN ═══
//
// ⛔ NEVER READ tax_rate RAW. parcel_jurisdiction_rates stores the value AS ENTERED plus its unit, because
// Florida TRIM notices quote MILLAGE (6.5 mills) and California quotes PERCENT (1.178467%), and normalising
// on save would turn a mis-set toggle into a stored number silently 10x wrong with no record of what was
// typed. A factor-of-ten on a rate is this codebase's most frequent error class — three times in two weeks.
// So conversion happens on every read, here, once.
//
// Returns PERCENT, the unit total_tax_rate and computeTaxBill's `rate` already use. Null when there is no
// usable rate, never 0: a rate of zero would compute a tax of zero and present it as an answer.
export function ratePercentOf(rateRow) {
  if (!rateRow) return null;
  const raw = rateRow.tax_rate;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n === 0) return null;
  return rateRow.rate_unit === 'mills' ? n / 10 : n;   // 1 mill = $1 per $1,000 = 0.1%
}

// One taxable base and its rate, per jurisdiction — the shape aggregateTax() consumes.
//
// ⛔ THE FALLBACK IS WHAT MAKES THIS A NO-OP ON TODAY'S DATA. A parcel with one jurisdiction and no stored
// per-jurisdiction rate yields exactly one line at the collector's rate, so the arithmetic downstream is the
// number it has always been. A per-jurisdiction rate only ever ADDS precision where someone entered one.
//
// ⛔ AND A MISSING RATE IS NULL, NOT THE COLLECTOR'S, ONCE THERE ARE TWO BASES. With two bases the collector
// rate is the consolidated one — school + non-school added together — which applies to NEITHER base; using
// it for whichever column was left blank would overstate that column by the other's rate. aggregateTax skips
// a null-rate line and reports `partial`, so the card can say which jurisdiction is missing its rate instead
// of quietly answering with a wrong one.
//
// ⛔ A COLUMN TAKES ITS MARKED BASELINE'S RATE when none is saved under its own name (columnRateRow below).
export function jurisdictionTaxLines(rows, ratesByJurisdiction, collectorRatePercent, proposedRows) {
  const list = (rows || []).filter(Boolean);
  const single = list.length === 1;
  return list.map((r) => {
    const own = ratePercentOf(columnRateRow(r.jurisdiction, ratesByJurisdiction, proposedRows));
    return {
      jurisdiction: r.jurisdiction || null,
      taxableValue: r.taxable_total == null || r.taxable_total === '' ? null : Number(r.taxable_total),
      rate: own != null ? own : (single ? collectorRatePercent ?? null : null),
      rateSource: own != null ? 'jurisdiction' : (single ? 'collector' : 'missing'),
    };
  });
}

// ═══ THE TAX RATE ROW ENTRY FOR A COLUMN — ITS OWN, ELSE ITS MARKED BASELINE'S ═══
//
// ⛔ RATES ARE SAVED UNDER THE PROPOSED COLUMN NAMES (the Tax Rate row lives on Proposed), AND A FINAL LEVEL CAN NAME
// ITS COLUMNS DIFFERENTLY. 30-3129-029-0010 / 2025: Proposed County + Public Schools carry 10.2987 and 6.633 mills;
// Appeal One is County + School. Looked up by name, School had no rate, so the card read "computed unavailable — no
// rate for School" on a bill whose two bases reproduce it exactly (25,000,000 × 1.02987% + 25,000,000 × 0.6633% =
// 423,292.50). The savings baseline marker ("Public Schools" → "School") is a person saying these are one base,
// so the rate follows it. Never inferred from a name; no marker, no fallback.
export function columnRateRow(jurisdiction, savedRates, proposedRows) {
  const rates = savedRates || {};
  if (ratePercentOf(rates[jurisdiction]) != null) return rates[jurisdiction];
  const baseline = (proposedRows || []).find((r) => r && r.savings_baseline_for === jurisdiction);
  return baseline ? (rates[baseline.jurisdiction] || null) : null;
}
